import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';

import { Spinner } from '@/components/ui';
import { useAuth } from '@/features/auth/useAuth';
import { api } from '@/lib/api/endpoints';

interface GuardProps {
  children: ReactNode;
}

/**
 * Sends a signed-in user who has not finished onboarding to `/onboarding`.
 *
 * Separated from `RequireAuth` on purpose: conflating "has a session" with "has
 * a complete profile" is how redirect loops start. This guard assumes auth has
 * already been checked.
 */
export function RequireOnboarding({ children }: GuardProps) {
  const { isAuthenticated, isReady } = useAuth();

  const profileQuery = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api.me.get(signal),
    enabled: isReady && isAuthenticated,
    staleTime: 60_000,
  });

  if (!isReady || (isAuthenticated && profileQuery.isPending)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" label="Loading profile" />
      </div>
    );
  }

  if (isAuthenticated && profileQuery.data && !profileQuery.data.profile.is_onboarded) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

/**
 * Sends an already-onboarded user away from the onboarding wizard.
 */
export function SkipIfOnboarded({ children }: GuardProps) {
  const { isAuthenticated, isReady } = useAuth();

  const profileQuery = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api.me.get(signal),
    enabled: isReady && isAuthenticated,
    staleTime: 60_000,
  });

  if (!isReady || profileQuery.isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" label="Loading profile" />
      </div>
    );
  }

  if (profileQuery.data?.profile.is_onboarded) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
